import { z } from 'zod';
import { auditSchema, idSchema, isoDateTimeSchema } from '../../common.js';
import { guestNameSchema } from './guestCore.js';
/**
 * Games (WS-17a). A family registry (`GAMES_DOC`, `Y.Map<gameId, GameMeta>`) plus one
 * content doc per match (`game:<id>`) — same registry/content shape as `apps` and `lists`,
 * but deliberately *without* their audience machinery: games are family-visible by design
 * (spectating siblings is the point), so the content doc needs no `doc_access` row and
 * there's no personal registry to place a game in. See `docs/ARCHITECTURE.md` §1.3 — no
 * row means every authenticated household member can already open both docs.
 */
/** Yjs doc holding `Y.Map<gameId, GameMeta>` — every game, family-visible. */
export const GAMES_DOC = 'games';
/** Key of the `Y.Map` inside `GAMES_DOC`. */
export const GAMES_MAP_KEY = 'games';
/** One Yjs doc per match. Its move log lives at `GAME_MOVES_KEY`. */
export function gameDocName(id) {
    return `game:${id}`;
}
/**
 * Per-user companion doc for one match — where a game kind's **hidden information** lives
 * (WS-17c). Battleship keeps a player's ship placement and commit salt here.
 *
 * The name is the enforcement: `game:<id>:user:<userId>` is openable only by `<userId>`,
 * refused with a 403 at the WebSocket upgrade for anybody else, needing no `doc_access` row
 * and un-widenable by one — exactly the mechanism `calendar:user:`/`lists:user:` already use
 * (`../sharing/index.ts`, `personalDocOwnerId`). This is the hidden-information game the
 * WS-15 sharing layer was built for; the rule is one matcher, not a per-game feature.
 *
 * The shared `game:<id>` doc stays family-visible: shots, results, and the end-of-game
 * reveal are public, because spectating is the point.
 */
export function gameUserDocName(id, userId) {
    return `game:${id}:user:${userId}`;
}
/** Key of the `Y.Array<Connect4Move>` (or a future game kind's own move type) inside a
 * `game:<id>` content doc. */
export const GAME_MOVES_KEY = 'moves';
/** Key of the `Y.Map` inside a `game:<id>:user:<userId>` doc holding that player's secret
 * (Battleship: `{ placement, salt }`). */
export const GAME_SECRET_KEY = 'secret';
/**
 * Key of the `Y.Map` inside a `game:<id>` content doc holding the **guest seat** (WS-23):
 * `{ guestId, guestName, joinedAt }`, written by the guest themselves.
 *
 * It lives in the match doc rather than in the registry because the match doc is the only
 * doc a guest may write. Their id was already public (allocated at mint time, written into
 * `playerIds` by the creator); this carries the one thing that could not be known until they
 * opened the link — the name they chose. Members mirror it up into `GameMeta.guestNames` so
 * the surfaces that never open a match doc still say "Dan vs Sam" rather than "Dan vs Guest".
 */
export const GAME_SEAT_KEY = 'seat';
/**
 * Key of the `Y.Array<GameChatMessage>` inside a `game:<id>` content doc: the players' chat
 * (smack talk) for that match. Lives in the match doc so a guest player can chat too, and so
 * it travels with the game: spectators can read it, only players write it.
 */
export const GAME_CHAT_KEY = 'chat';
export const GAME_CHAT_MAX = 280;
export const gameChatMessageSchema = z.object({
    id: z.string().min(1).max(64),
    authorId: idSchema,
    text: z.string().min(1).max(GAME_CHAT_MAX),
    at: isoDateTimeSchema,
    /**
     * Marks the announcement line of a **sealed note** (friend of the family): the author put a
     * short secret in a Handoff mailbox and this line only says one exists. The spoken code
     * that opens it is never in the chat — spectators can read the match doc — so it travels
     * out of band. Optional/back-compat: an older client just renders the line as text.
     */
    sealed: z.literal(true).optional(),
});
export const gameGuestSeatSchema = z.object({
    guestId: idSchema,
    guestName: guestNameSchema,
    joinedAt: isoDateTimeSchema,
});
/** Extensible: each kind brings its own move shape and its own `derive`. */
export const GAME_KINDS = [
    'connect4',
    'battleship',
    'checkers',
    'pool',
    'beerpong',
    'mancala',
];
/** Extensible: `Connect4Move` is the only move shape today; a future kind adds its own. */
export const gameKindSchema = z.enum(GAME_KINDS);
/** Display name for a kind — the picker, the archive card, the board header. */
export function gameKindLabel(kind) {
    switch (kind) {
        case 'connect4':
            return 'Connect 4';
        case 'battleship':
            return 'Battleship';
        case 'checkers':
            return 'Checkers';
        case 'pool':
            return 'Pool';
        case 'beerpong':
            return 'Beer Pong';
        case 'mancala':
            return 'Mancala';
    }
}
export const GAME_STATUSES = ['open', 'active', 'done'];
export const gameStatusSchema = z.enum(GAME_STATUSES);
export const gameMetaSchema = auditSchema.extend({
    id: idSchema,
    kind: gameKindSchema,
    title: z.string().min(1).max(200),
    /**
     * Exactly two players — Connect-4 and every kind planned for v1 are 2-player. Since WS-23
     * a seat may hold a **guest id** (`guest_<random>`, `isGuestId`) rather than a member id;
     * nothing here changes, because the tuple was always opaque ids and every consumer resolves
     * a name through `resolveGamePlayer()`.
     */
    playerIds: z.tuple([idSchema, idSchema]),
    status: gameStatusSchema,
    /**
     * Chosen display names for any guest sitting in this game (WS-23), keyed by guest id.
     * Optional and back-compat: a game with no guest has no entry, and a guest who has claimed
     * their link but whose name has not been mirrored up from the match doc's `GAME_SEAT_KEY`
     * yet simply renders as "Guest".
     */
    guestNames: z.record(idSchema, guestNameSchema).optional(),
    winnerId: idSchema.optional(),
    /** Stamped once, when `status` first flips to `'done'` (WS-17b) — the archive's "date
     * finished". Optional/back-compat: games finished before WS-17b have no `finishedAt`; the
     * archive falls back to `updatedAt` for those rather than treating the field as required. */
    finishedAt: isoDateTimeSchema.optional(),
    /** Set alongside `status: 'done'` when a finished game auto-spawned its own successor (Pool's
     * early-eight foul: the loser didn't lose the *match*, just the rack, so play continues
     * without a manual Rematch tap). Points at the fresh `GameMeta.id`; both players' clients
     * follow it once they see it. Optional/back-compat: every other ending is still the manual
     * Rematch button. */
    nextGameId: idSchema.optional(),
});
/** Auto title, e.g. "Dan vs Faye" — display names only, never ids. */
export function buildGameTitle(playerAName, playerBName) {
    return `${playerAName} vs ${playerBName}`;
}
/** In-app route for a game's board. */
export function gamePostPath(id) {
    return `/games/${id}`;
}
export * from './guestCore.js';
export * from './connect4.js';
export * from './battleship.js';
export * from './sha256.js';
export * from './checkers.js';
export * from './pool.js';
export * from './beerpong.js';
export * from './mancala.js';
export * from './replay.js';
export * from './summary.js';
//# sourceMappingURL=index.js.map